import React, { useMemo, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Add, Delete, Edit, Refresh, Send } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { 
  useGetPushTemplatesQuery,
  useDeletePushTemplateMutation,
  pushTemplatesApi
} from '../../redux/api/pushTemplatesApi';
import { PushTemplate, PushTemplateType } from '../../resources/types/pushTemplatesTypes';
import PushTemplateDialog from './PushTemplateDialog';
import { sendPushAsync } from '../../redux/slices/usersSlice';
import { SendPushBody, UserType } from '../../resources/types/usersTypes';
import UserTypeSelect from '../../containers/UserTypeSelect';
import { InputChangeEvent } from '../../resources/utils';
import moment from 'moment';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectCities } from '../../redux/slices/citiesSlice';

const PREFIX = 'PushTemplatesTab';

const classes = {
  header: `${PREFIX}-header`,
  search: `${PREFIX}-search`,
  emptyState: `${PREFIX}-emptyState`,
  typeChip: `${PREFIX}-typeChip`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.header}`]: {
    marginBottom: 20
  },
  [`& .${classes.search}`]: {
    marginBottom: 20,
    width: 300
  },
  [`& .${classes.emptyState}`]: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  [`& .${classes.typeChip}`]: {
    borderRadius: '8px'
  }
}));

const getTypeLabel = (type: PushTemplateType, t: TFunction): string => {
  const labels: Record<PushTemplateType, string> = {
    system: t('pushes.typeSystem'),
    marketing: t('pushes.typeMarketing'),
    reward: t('pushes.typeReward'),
    custom: t('pushes.typeCustom')
  };
  return labels[type] || type;
};

const getTypeColor = (type: PushTemplateType) => {
  const colors: Record<PushTemplateType, string> = {
    system: '#2196F3',
    marketing: '#FF9800',
    reward: '#4CAF50',
    custom: '#9C27B0'
  };
  return colors[type] || '#757575';
};

const PushTemplatesTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const admins = useSelector(selectAdmins);
  const cities = useSelector(selectCities);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<PushTemplateType | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<PushTemplate | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<PushTemplate | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Send template state
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [templateToSend, setTemplateToSend] = useState<PushTemplate | null>(null);
  const [sendUserType, setSendUserType] = useState<UserType>('');
  const [isSending, setIsSending] = useState(false);

  const { data: templates = [], isLoading, refetch, isFetching } = useGetPushTemplatesQuery();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeletePushTemplateMutation();

  const currentCityName = useMemo(() => {
    const city = cities.items.find(c => c._id === admins.currentCity);
    return city?.cityName || t('pushes.notSelected');
  }, [cities.items, admins.currentCity, t]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(template => 
        template.title?.toLowerCase().includes(query) ||
        template.body?.toLowerCase().includes(query)
      );
    }

    if (typeFilter !== 'all') {
      result = result.filter(template => template.type === typeFilter);
    }

    return result;
  }, [templates, searchQuery, typeFilter]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenDialog = useCallback(() => {
    setEditTemplate(null);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditTemplate(null);
  }, []);

  const handleEditTemplate = useCallback((template: PushTemplate) => {
    setEditTemplate(template);
    setDialogOpen(true);
  }, []);

  const handleOpenDeleteConfirm = useCallback((template: PushTemplate) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  }, []);

  const handleDeleteTemplate = useCallback(async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate(templateToDelete._id).unwrap();
      setSnackbarMessage(t('pushes.templateDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
      refetch();
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || t('pushes.errorDeleteTemplate');
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [templateToDelete, deleteTemplate, refetch, t]);

  const handleSuccess = useCallback((message?: string) => {
    setSnackbarMessage(message || t('pushes.templateSaved'));
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  }, [t]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const handleOpenSendDialog = useCallback((template: PushTemplate) => {
    setTemplateToSend(template);
    setSendUserType('');
    setSendDialogOpen(true);
  }, []);

  const handleCloseSendDialog = useCallback(() => {
    setSendDialogOpen(false);
    setTemplateToSend(null);
    setSendUserType('');
  }, []);

  const handleSendTemplate = useCallback(async () => {
    if (!templateToSend) return;

    setIsSending(true);
    const body: SendPushBody = { text: templateToSend.body };
    if (sendUserType) body.userType = sendUserType;

    await dispatch(sendPushAsync(
      body,
      () => {
        // Success callback - only runs if API call succeeds
        dispatch(pushTemplatesApi.util.invalidateTags(['PushHistory']));
        setSnackbarMessage(t('pushes.pushSentSuccess'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleCloseSendDialog();
        setIsSending(false);
      },
      (err: any) => {
        // Error callback - runs if API call fails
        const errorMessage = err?.response?.data?.message || err?.message || t('pushes.errorSending');
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsSending(false);
      }
    ));
  }, [templateToSend, sendUserType, dispatch, handleCloseSendDialog, t]);

  if (isLoading) {
    return (
      <Root>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      <Grid container alignItems='center' className={classes.header} spacing={2}>
        <Grid item>
          <Button
            color='primary'
            startIcon={<Add />}
            variant='contained'
            onClick={handleOpenDialog}
          >
            {t('pushes.newTemplate')}
          </Button>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
            variant='outlined'
            onClick={handleRefresh}
            disabled={isFetching}
          >
            {t('pushes.refresh')}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('pushes.searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pushes.filterByType')}</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as PushTemplateType | 'all')}
              label={t('pushes.filterByType')}
            >
              <MenuItem value="all">{t('pushes.allTypesTemplates')}</MenuItem>
              <MenuItem value="system">{t('pushes.typeSystem')}</MenuItem>
              <MenuItem value="marketing">{t('pushes.typeMarketing')}</MenuItem>
              <MenuItem value="reward">{t('pushes.typeReward')}</MenuItem>
              <MenuItem value="custom">{t('pushes.typeCustom')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {filteredTemplates.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">
              {searchQuery || typeFilter !== 'all' 
                ? t('pushes.emptyFilteredTemplates') 
                : t('pushes.emptyTemplates')}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('pushes.colTitle')}</TableCell>
                <TableCell>{t('pushes.colType')}</TableCell>
                <TableCell>{t('pushes.colMessageText')}</TableCell>
                <TableCell>{t('pushes.colUpdatedAt')}</TableCell>
                <TableCell align="right">{t('pushes.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map(template => (
                <TableRow key={template._id}>
                  <TableCell>
                    <strong>{template.title}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(template.type, t)}
                      size="small"
                      className={classes.typeChip}
                      sx={{
                        backgroundColor: getTypeColor(template.type),
                        color: '#fff'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{
                        maxWidth: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {template.body}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {moment(template.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip title={t('pushes.tooltipSend')} arrow>
                      <IconButton 
                        size='small' 
                        onClick={() => handleOpenSendDialog(template)}
                        sx={{ color: '#5073F8' }}
                      >
                        <Send fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('pushes.tooltipEdit')} arrow>
                      <IconButton 
                        size='small' 
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('pushes.tooltipDelete')} arrow>
                      <IconButton 
                        size='small' 
                        onClick={() => handleOpenDeleteConfirm(template)}
                        sx={{ color: 'error.main' }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PushTemplateDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        editTemplate={editTemplate}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
      >
        <DialogTitle>{t('pushes.confirmDeletionTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('pushes.confirmDeleteTemplate', { title: templateToDelete?.title })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={isDeleting}>
            {t('pushes.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteTemplate} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? t('pushes.deleting') : t('pushes.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendDialogOpen}
        onClose={handleCloseSendDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('pushes.sendMessageTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                🏙️ {t('pushes.cityLabel', { city: currentCityName })}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('pushes.messageTextLabel')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              {templateToSend?.body}
            </Typography>
            <UserTypeSelect
              allOption
              value={sendUserType}
              onChange={(e: InputChangeEvent) =>
                setSendUserType(e.target.value as UserType)
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendDialog} disabled={isSending}>
            {t('pushes.cancel')}
          </Button>
          <Button 
            onClick={handleSendTemplate} 
            color="primary" 
            variant="contained"
            disabled={isSending}
          >
            {isSending ? t('pushes.sending') : t('pushes.sendBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Root>
  );
};

export default PushTemplatesTab;
